import * as React from 'react';
import { styled, css } from '@mui/material/styles';
import Downshift from 'downshift';
import MenuItem from '@mui/material/MenuItem';
import { compose } from 'redux';
import { IconCross, IconSearch, IconVideoCamera } from '@maiia/design-system';
import { kebabCase } from 'lodash';
import { Box, Stack, Typography, Button } from '@mui/material';
import sanitize from 'sanitize-html';
import { useTranslation } from '@/src/i18n';
import Input from '../../atoms/Input/Input';
import Paper from '../../atoms/Paper/Paper';
import Avatar from '../../atoms/Avatar/Avatar';
import HighlightText from '@/components/atoms/HighlightText/HighlightText';
import MenuItemDivider from '../Autocomplete/MenuItemDivider';
import { formatAddressLine } from '../AddressItem/AddressItem';
import UserLocationButtons from '../UserLocationButtons/UserLocationButtons';

const Root = styled('div')(
  ({ theme }) => css`
    & .Autocomplete-root {
      flex-grow: 1;
    }

    & .Autocomplete-container {
      flex-grow: 1;
      position: relative;
    }

    & .Autocomplete-paper {
      z-index: 1;
      margin-top: ${theme.spacing(1)};
      left: 0;
      right: 0;
    }

    & .Autocomplete-inputRoot {
      flex-wrap: wrap;
    }

    & .Autocomplete-inputInput {
      width: auto;
      flex-grow: 1;
    }

    & .Autocomplete-divider {
      height: ${theme.spacing(2)};
    }
  `,
);

const CardTitles = styled('div')(css`
  display: flex;
  flex-direction: row;
  width: 100%;
  line-height: normal;
`);

const MenuItemTitle = styled('p')(
  ({ theme }) => css`
    line-height: ${theme.spacing(3)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
);

const MenuItemSubtitle = styled('p')(
  ({ theme }) => css`
    color: ${theme.palette.text.medium};
    overflow-x: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
);

const MenuItemCity = styled('span')(css`
  font-style: italic;
`);

type SuggestionType =
  | 'speciality'
  | 'expertise'
  | 'center'
  | 'practitioner'
  | undefined;

const useGetAutocompleteItemValues = (suggestion: any) => {
  const { t } = useTranslation();
  const { pictureS3Id, _highlightResult } = suggestion;
  const suggestionType: SuggestionType = suggestion.objectType?.toLowerCase();
  const professionalTitle =
    suggestion.isParamedical === false ? t('professional_title') : '';

  switch (suggestionType) {
    case 'practitioner':
      return {
        avatar: pictureS3Id
          ? `/files/${pictureS3Id}`
          : `/static/images/default_${suggestionType}.svg`,
        title: `${professionalTitle}${_highlightResult?.firstName.value} ${_highlightResult?.lastName.value}`,
        subtitle: `${suggestion.speciality}${suggestion.city ? ', ' : ''}`,
        city: suggestion.city,
        label: suggestion.label,
      };

    case 'center':
      return {
        avatar: pictureS3Id
          ? `/files/${pictureS3Id}`
          : '/static/images/default_center.svg',
        title: _highlightResult?.name.value,
        subtitle: undefined,
        city: suggestion.city,
        label: suggestion.label,
      };

    case 'speciality':
    case 'expertise':
      return {
        avatar: undefined,
        title: _highlightResult?.name.value || suggestion.label,
        subtitle: undefined,
        city: undefined,
        label: suggestion.label,
      };

    // Case of an address AND a drug... (TODO : see with backend to add an objectType to these objects)
    case undefined:
      // Temporary, until the backend sends a objectType to differentiate the 2 cases
      // Case of an address
      if (suggestion.citycode || suggestion.city) {
        return {
          avatar: undefined,
          title: formatAddressLine(suggestion.rawData),
          subtitle: undefined,
          city: undefined,
          label: undefined,
        };
      }

      // Case of a drug
      return {
        avatar: undefined,
        title: suggestion.label,
        subtitle: undefined,
        city: undefined,
        label: suggestion.label,
      };

    // Case that shouldn't occur
    default:
      return {
        avatar: undefined,
        title: undefined,
        subtitle: undefined,
        city: undefined,
        label: undefined,
      };
  }
};

const Suggestion = suggestionProps => {
  const {
    suggestion,
    index,
    itemProps,
    highlightedIndex,
    selectedItem,
    isTlcAvailable,
    searchFilter,
    useHighlight,
  } = suggestionProps;
  const isHighlighted = highlightedIndex === index;
  const isSelected = selectedItem ? selectedItem.id === suggestion.id : false;
  const { t } = useTranslation();
  const { avatar, title, subtitle, city, label } = useGetAutocompleteItemValues(
    suggestion,
  );

  return (
    <>
      <MenuItem
        {...itemProps}
        key={suggestion.id}
        selected={isHighlighted}
        data-cy={`suggestion-item-${kebabCase(label)}`}
        component="div"
        sx={theme => ({
          display: 'flex',
          fontWeight: isSelected ? 500 : 400,
          paddingTop: theme.spacing(1),
          minHeight: theme.spacing(5),
        })}
        style={{
          border: suggestion.name !== 'TLC' && 'none', // TODO: refactor _searchbar.scss then move it to sx attribute
        }}
      >
        <CardTitles>
          {suggestion.name === 'TLC' ? (
            <Button
              fullWidth
              variant="text"
              color="secondary"
              startIcon={<IconVideoCamera id="teleconsult-now-icon" />}
              sx={theme => ({
                justifyContent: 'left',
                paddingLeft: 0,
                fontSize: theme.typography.body1.fontSize,
              })}
            >
              {t(suggestion.label)}
            </Button>
          ) : (
            <Stack
              direction="row"
              spacing={2}
              sx={{ width: '100%', maxWidth: '100%', flexWrap: 'nowrap' }}
            >
              {avatar && (
                <Box sx={{ flexShrink: 0 }}>
                  <Avatar
                    source={avatar}
                    isOnline={isTlcAvailable}
                    size="small"
                  />
                </Box>
              )}
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                sx={{
                  overflow: 'hidden',
                  flexGrow: 1,
                }}
              >
                {!useHighlight && (
                  <MenuItemTitle
                    dangerouslySetInnerHTML={{
                      __html: sanitize(title, { allowedTags: ['em'] }),
                    }}
                  />
                )}
                {useHighlight && (
                  <Typography
                    sx={theme => ({
                      lineHeight: theme.spacing(3),
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    })}
                  >
                    <HighlightText text={title} term={searchFilter} />
                  </Typography>
                )}
                {(subtitle || city) && (
                  <MenuItemSubtitle>
                    <span>{subtitle}</span>
                    <MenuItemCity>{city}</MenuItemCity>
                  </MenuItemSubtitle>
                )}
              </Box>
            </Stack>
          )}
        </CardTitles>
      </MenuItem>
      {suggestion.divider && <MenuItemDivider />}
    </>
  );
};

type Props = {
  selectedItemIndex?: number;
  suggestions: Array<any>;
  onSearch: Function;
  onChange: any;
  label?: string;
  placeholder: string;
  initialInputValue?: any;
  iconComponent?: any;
  allowClear?: boolean;
  focusInput?: boolean;
  filterValue?: string;
  useHighlight?: boolean;
  maxLength?: number;
  showQuickAddress?: boolean;
  topElement?: React.ReactNode;
};

type State = {
  menuIsOpen: boolean;
};

class IntegrationDownshift extends React.Component<Props, State> {
  componentDidMount() {
    const { focusInput } = this.props;
    if (focusInput && this.inputRef?.current) this.inputRef.current.focus();
  }

  componentDidUpdate() {
    const { selectedItemIndex, suggestions } = this.props;
    if (!selectedItemIndex) return;
    if (
      !Number.isInteger(selectedItemIndex) ||
      selectedItemIndex < 0 ||
      suggestions.length <= selectedItemIndex ||
      !this.downshiftRef.current
    ) {
      return;
    }
    const {
      state: { selectedItem },
      selectItem,
    } = this.downshiftRef.current;
    if ((selectedItem || {}).id !== suggestions[selectedItemIndex].id) {
      return selectItem(suggestions[selectedItemIndex]);
    }
  }

  onListTouchStart = e => {
    e.preventDefault();
    if (document.activeElement) (document.activeElement as any).blur();
  };

  onClear = () => {
    this.downshiftRef.current?.clearSelection();
  };

  downshiftRef = React.createRef<any>();

  inputRef = React.createRef<any>();

  render() {
    const {
      onSearch,
      onChange = () => {},
      suggestions,
      placeholder,
      initialInputValue,
      iconComponent,
      allowClear,
      filterValue,
      useHighlight,
      maxLength,
      showQuickAddress,
      topElement,
    } = this.props;

    return (
      <Root>
        <Downshift
          ref={this.downshiftRef}
          id="downshift-simple"
          itemToString={item => (item ? item.label : '')}
          onChange={onChange}
          initialInputValue={initialInputValue}
          isOpen
          onSelect={() => {
            this.inputRef?.current?.focus();
          }}
        >
          {({
            getInputProps,
            getItemProps,
            getMenuProps,
            highlightedIndex,
            isOpen,
            closeMenu,
            selectedItem,
            setState,
          }) => (
            <div className="Autocomplete-container">
              <Input
                {...getInputProps({
                  onChange: e => {
                    onSearch(e.target.value);
                  },
                })}
                placeholder={placeholder}
                onFocus={e => {
                  window.scrollTo({
                    top:
                      e.currentTarget.getBoundingClientRect().top +
                      window.scrollY -
                      60,
                    behavior: 'smooth',
                  });
                }}
                // If I remove this onBlur, it will be called after setState({ inputValue }) in the User Location Button and remove the value.
                onBlur={() => {}}
                ref={this.inputRef}
                // We need the "no-icon" to remove padding-right from Sass
                variant={iconComponent === null ? 'no-icon' : 'with-icon'}
                iconComponent={
                  iconComponent !== null && (iconComponent || <IconSearch />)
                }
                maxLength={maxLength}
              />
              <div {...getMenuProps()}>
                {isOpen ? (
                  // @ts-ignore
                  <Paper onTouchStart={this.onListTouchStart} tabIndex={0}>
                    {showQuickAddress && (
                      <UserLocationButtons
                        onSelectItem={item => {
                          // selectItem triggers a warning in the console
                          // selectItem(item);
                          setState({
                            selectedItem: item,
                            inputValue: item.label,
                          });
                          closeMenu();
                        }}
                        onSearch={(value: string) => {
                          onSearch(value);
                          setState({ inputValue: value });
                        }}
                      />
                    )}
                    {topElement}
                    {suggestions.map((suggestion, index) => (
                      <Suggestion
                        key={suggestion.id}
                        suggestion={suggestion}
                        index={index}
                        itemProps={getItemProps({ item: suggestion })}
                        highlightedIndex={highlightedIndex}
                        selectedItem={selectedItem}
                        centerId={suggestion.centerId}
                        objectID={suggestion.objectID}
                        isTlcAvailable={suggestion.isTlcAvailable}
                        searchFilter={filterValue || getInputProps().value}
                        useHighlight={useHighlight}
                      />
                    ))}
                  </Paper>
                ) : null}
              </div>
              <div className="right-icon">
                {allowClear && getInputProps().value !== '' && (
                  <IconCross onClick={this.onClear} />
                )}
              </div>
            </div>
          )}
        </Downshift>
      </Root>
    );
  }
}

export default compose()(IntegrationDownshift);
